// Pure rules behind the Seats screen — mirrors ExtensionAllocationService's
// validations so an operator sees the problem before submitting. The server
// remains the authority and its message is shown when it disagrees.
import type { SeatOverviewRow } from 'api/services/seatAllocations.service';

export interface SeatDraft {
  organizationId: string;
  rangeStart: string;
  rangeEnd: string;
  maxSeats: string;
  enabled: boolean;
  notes: string;
}

const whole = (s: string): number | null => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : null);

/** Both range fields left blank: the server picks the range. */
export function isAutoRange(d: SeatDraft): boolean {
  return d.rangeStart.trim() === '' && d.rangeEnd.trim() === '';
}

/** First problem with a draft, or null when the server should accept it. */
export function validateDraft(d: SeatDraft, usedSeats: number): string | null {
  if (!d.organizationId) return 'Choose an organisation.';
  const max = whole(d.maxSeats);
  if (max === null) return 'Seat count must be a whole number.';
  if (max < usedSeats) {
    return `This organisation already uses ${usedSeats} seats; maxSeats cannot be set below that.`;
  }
  // Both range fields blank = let the system choose (1000 upward, covering existing
  // seats). Extensions are per business, so ranges never clash between businesses.
  if (isAutoRange(d)) return null;
  const start = whole(d.rangeStart);
  const end = whole(d.rangeEnd);
  if (start === null || end === null) return 'Range must be whole numbers, or leave both blank for automatic.';
  if (start < 10) return 'rangeStart must be at least 10.';
  if (end < start) return 'rangeEnd must not be below rangeStart.';
  const width = end - start + 1;
  if (max > width) return `maxSeats (${max}) exceeds the ${width} numbers in ${start}-${end}.`;
  if (max < usedSeats) {
    return `This organisation already uses ${usedSeats} seats; maxSeats cannot be set below that.`;
  }
  return null;
}

/** A draft pre-filled from a row (edit) or blank (new grant). */
export function draftFor(row?: SeatOverviewRow | null): SeatDraft {
  const a = row?.allocation;
  return {
    organizationId: row?.organizationId ?? '',
    rangeStart: a ? String(a.rangeStart) : '',
    rangeEnd: a ? String(a.rangeEnd) : '',
    maxSeats: a ? String(a.maxSeats) : '',
    enabled: a ? a.enabled !== false : true,
    notes: a?.notes ?? '',
  };
}

/** What the Status column says. */
export function statusOf(row: SeatOverviewRow): string {
  if (row.platformRoot) return 'Platform (unlimited softphone)';
  if (!row.allocation) return 'No seats';
  if (row.allocation.enabled === false) return 'Disabled';
  if (row.allocation.maxSeats <= 0) return 'No seats';
  return row.usedSeats >= row.allocation.maxSeats ? 'Full' : 'Active';
}
